import dayjs from 'dayjs';
import API_CONFIG from '../config/api';
import { getAuthorizedCompanyContext } from '../utils/companyContext';

export function ecommerceRequestHeaders(): Record<string, string> {
  const companyId = getAuthorizedCompanyContext();
  return {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json',
    ...(companyId ? { 'X-Company-Id': String(companyId) } : {}),
  };
}

export function formatOrderTimestamp(value: string): string {
  const date = dayjs(value);
  return date.isValid() ? date.format('DD/MM/YYYY · HH:mm') : 'Date indisponible';
}

export function shortenOrderReference(value: string): string {
  return value.length > 18 ? `${value.slice(0, 3)}…${value.slice(-5)}` : value;
}

export type ProductOnlinePatch = {
  online_visibility?: boolean;
  online_featured?: boolean;
};
export type SelectedProductUpdateResult = {
  updatedIds: number[];
  failures: { id: number; message: string }[];
  skippedIds: number[];
};

// Réutilise la transaction, les permissions, les variantes et le quota du PUT
// unitaire. Une sélection n'est pas une transaction globale : les échecs restent
// identifiés et aucune requête suivante ne part après un changement de contexte.
export async function updateSelectedProducts({
  ids, patch, warehouseId, storeId, shouldContinue, request = fetch,
}: {
  ids: number[];
  patch: ProductOnlinePatch;
  warehouseId: number;
  storeId: number | null;
  shouldContinue: () => boolean;
  request?: typeof fetch;
}): Promise<SelectedProductUpdateResult> {
  const keys = Object.keys(patch);
  if (!Number.isInteger(warehouseId) || warehouseId <= 0 ||
      (storeId !== null && (!Number.isInteger(storeId) || storeId <= 0)) ||
      !ids.length || ids.some((id) => !Number.isInteger(id) || id <= 0) ||
      keys.length !== 1 || !['online_visibility', 'online_featured'].includes(keys[0]) ||
      typeof patch[keys[0] as keyof ProductOnlinePatch] !== 'boolean') {
    throw new Error('Sélection ou paramètres de mise à jour invalides.');
  }
  const selectedIds = Array.from(new Set(ids));
  const result: SelectedProductUpdateResult = { updatedIds: [], failures: [], skippedIds: [] };
  const headers = ecommerceRequestHeaders();
  for (let index = 0; index < selectedIds.length; index += 1) {
    if (!shouldContinue()) {
      result.skippedIds = selectedIds.slice(index);
      break;
    }
    const id = selectedIds[index];
    try {
      const response = await request(`${API_CONFIG.BASE_URL}/api/produits/${id}/online`, {
        method: 'PUT', headers,
        body: JSON.stringify({ ...patch, warehouse_id: warehouseId, ...(storeId ? { store_id: storeId } : {}) }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) result.updatedIds.push(id);
      else {
        result.failures.push({ id, message: data.error || `Mise à jour refusée (${response.status}).` });
        if (response.status === 401 || response.status === 403) {
          result.skippedIds = selectedIds.slice(index + 1);
          break;
        }
      }
    } catch {
      result.failures.push({ id, message: 'Réponse non reçue. Actualisez le catalogue pour vérifier le résultat.' });
    }
  }
  return result;
}
